import { createStructuredSelector } from 'reselect';
import { selectAccountID } from '../../../../selectors/globalSelectors';
import { getCarouselData } from '../../../../CommonComponents/Carousel/selectors';

/** Grants Overview Selector */
export const selectGrantsOverviewStates = (state) => state.grantsDashboard;

/** Messaging State Selector */
export const getMessagingStates = (state) => state.messaging;

/** Account data */
export const selectAccount = (state) => {
  const { accountData } = selectGrantsOverviewStates(state);
  return accountData;
};

export const selectAccountData = (state) => {
  const { data } = selectAccount(state);
  return data;
};

export const selectAccountLoadingStatus = (state) => {
  const { isLoading } = selectAccount(state);
  return isLoading;
};

export const selectAccountError = (state) => {
  const { error } = selectAccount(state);
  return error;
};

/** Pending Grants */
export const selectPendingGrants = (state) => {
  const { currentPendingGrants } = selectGrantsOverviewStates(state);
  return currentPendingGrants;
};

export const selectPendingGrantsData = (state) => {
  const { data } = selectPendingGrants(state);
  return data;
};

export const selectPendingGrantsLoadingStatus = (state) => {
  const { isLoading } = selectPendingGrants(state);
  return isLoading;
};

export const selectPendingDataAvailable = (state) => {
  const { dataAvailableForNextRequest } = selectPendingGrants(state);
  return dataAvailableForNextRequest;
};

/** Issued Grants */
export const selectIssuedGrants = (state) => {
  const { issuedGrants } = selectGrantsOverviewStates(state);
  return issuedGrants;
};

export const selectIssuedGrantsData = (state) => {
  const { data } = selectIssuedGrants(state);
  return data;
};

export const selectIssuedGrantsLoadingStatus = (state) => {
  const { isLoading } = selectIssuedGrants(state);
  return isLoading;
};

export const selectIssuedDataAvailable = (state) => {
  const { dataAvailableForNextRequest } = selectIssuedGrants(state);
  return dataAvailableForNextRequest;
};

/** One Year Chart Data */
export const selectOneYearChartData = (state) => {
  const { grantDashboard_data } = selectGrantsOverviewStates(state);
  return grantDashboard_data;
};

export const selectOneYearChartDataLoadingStatus = (state) => {
  const { isLoadingBot } = selectOneYearChartData(state);
  return isLoadingBot;
};

export const selectOneYearChartDataValue = (state) => {
  const { data } = selectOneYearChartData(state);
  return data;
};

export const selectOneYearChartStatusText = (state) => {
  const { botStatusText } = selectOneYearChartData(state);
  return botStatusText;
};

export const selectOneYearY3Flag = (state) => {
  const { data_y3 } = selectOneYearChartData(state);
  return data_y3;
};

export const selectOneYearChartY5Flag = (state) => {
  const { data_y5 } = selectOneYearChartData(state);
  return data_y5;
};

/** Five Year Chart Data */
export const selectFiveYearChartData = (state) => {
  const { grantDashboard_data_all } = selectGrantsOverviewStates(state);
  return grantDashboard_data_all;
};

export const selectFiveYearChartDataLoadingStatus = (state) => {
  const { isLoadingBot } = selectFiveYearChartData(state);
  return isLoadingBot;
};

export const selectFiveYearChartDataValue = (state) => {
  const { data } = selectFiveYearChartData(state);
  return data;
};

export const selectFiveYearChartY3Flag = (state) => {
  const { data_all_y3 } = selectFiveYearChartData(state);
  return data_all_y3;
};

export const selectFiveYearChartY5Flag = (state) => {
  const { data_all_y5 } = selectFiveYearChartData(state);
  return data_all_y5;
};

/** Lazy loading data */
export const selectLazyLoadingStatus = (state) => {
  const { isLazyLoading } = selectGrantsOverviewStates(state);
  return isLazyLoading;
};

/** Chart Data */

export const selectChartData = (state) => {
  const { chartData } = selectGrantsOverviewStates(state);
  return chartData;
};

export const selectChartDataToShow = (state) => {
  const { show } = selectChartData(state);
  return show;
};

export const selectChartDataLoadingStatus = (state) => {
  const { isLoading } = selectChartData(state);
  return isLoading;
};

export const selectChartDataValue = (state) => {
  const { data } = selectChartData(state);
  return data;
};

export const selectChartDataError = (state) => {
  const { error } = selectChartData(state);
  return error;
};

/** Grants Count data */
export const selectGrantsCount = (state) => {
  const { grantsCount } = selectGrantsOverviewStates(state);
  return grantsCount;
};

export const selectGrantsCountData = (state) => {
  const { data } = selectGrantsCount(state);
  return data;
};

export const selectGrantsCountDataLoadingStatus = (state) => {
  const { isLoading } = selectGrantsCount(state);
  return isLoading;
};

/** Issued Grants */
export const selectIssuedPagination = (state) => {
  const { initialIssuedGrants } = selectGrantsOverviewStates(state);
  return initialIssuedGrants;
};

/** Pending Grants */
export const selectPendingPagination = (state) => {
  const { initialPendingGrants } = selectGrantsOverviewStates(state);
  return initialPendingGrants;
};

/** Messaging Data */
export const selectEstablishedDate = (state) => {
  const { messages } = getMessagingStates(state);
  return messages.additionalAccountDetails;
};

export const grantsOverviewStates = createStructuredSelector({
  accData: selectAccountData,
  accIsLoading: selectAccountLoadingStatus,
  accIsError: selectAccountError,
  currentPendingGrants: selectPendingGrantsData,
  isLoadingCurrentPendingGrants: selectPendingGrantsLoadingStatus,
  issuedGrants: selectIssuedGrantsData,
  isLoadingIssuedGrants: selectIssuedGrantsLoadingStatus,
  completedDataAvailable: selectIssuedDataAvailable,
  isGrantsLoading: selectOneYearChartDataLoadingStatus,
  grantsData: selectOneYearChartDataValue,
  botStatusText: selectOneYearChartStatusText,
  data_y3: selectOneYearY3Flag,
  data_y5: selectOneYearChartY5Flag,
  isAllGrantsLoading: selectFiveYearChartDataLoadingStatus,
  allGrantsData: selectFiveYearChartDataValue,
  accountID: selectAccountID,
  carouselData: getCarouselData,
  pendingDataAvailable: selectPendingDataAvailable,
  data_all_y3: selectFiveYearChartY3Flag,
  data_all_y5: selectFiveYearChartY5Flag,
  isLazyLoading: selectLazyLoadingStatus,
  showData: selectChartDataToShow,
  isLoadingGrants: selectChartDataLoadingStatus,
  selectedIssuedGrants: selectChartDataValue,
  selectedIsssuedGrantsError: selectChartDataError,
  grantsCount: selectGrantsCountData,
  grantsCountIsLoading: selectGrantsCountDataLoadingStatus,
  issuedPagination: selectIssuedPagination,
  currentPending: selectPendingPagination,
  establishedDate: selectEstablishedDate,
});
